<script>
import { mapActions, mapState } from 'vuex';
import {
  ReloadOutlined,
  PlayCircleOutlined,
  DesktopOutlined,
  TabletOutlined,
  MobileOutlined,
  WarningOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import bem from '@/utils/bem';
import PageHeader from '@/components/ux/pageHeader/page-header.vue';

const b = bem('sessions-list');

export default {
  components: {
    PageHeader,
    ReloadOutlined,
    PlayCircleOutlined,
    DesktopOutlined,
    TabletOutlined,
    MobileOutlined,
    WarningOutlined,
  },
  data() {
    return {
      title: 'Session Replay',
      filters: {
        period: '7d',
        dateRange: [],
        deviceType: null,
        hasErrors: null,
        search: '',
      },
      pagination: {
        page: 1,
        limit: 20,
      },
      periodOptions: [
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: '7d', label: 'Last 7 days' },
        { value: '30d', label: 'Last 30 days' },
        { value: 'custom', label: 'Custom' },
      ],
      deviceTypeOptions: [
        { value: null, label: 'All Devices' },
        { value: 'desktop', label: 'Desktop' },
        { value: 'tablet', label: 'Tablet' },
        { value: 'mobile', label: 'Mobile' },
      ],
      hasErrorsOptions: [
        { value: null, label: 'All Sessions' },
        { value: true, label: 'With Errors' },
        { value: false, label: 'Without Errors' },
      ],
      columns: [
        {
          title: 'Device',
          dataIndex: 'deviceType',
          key: 'deviceType',
          width: 80,
        },
        {
          title: 'User',
          dataIndex: 'userEmail',
          key: 'userEmail',
          ellipsis: true,
        },
        {
          title: 'Location',
          key: 'location',
        },
        {
          title: 'Started',
          dataIndex: 'startedAt',
          key: 'startedAt',
          width: 160,
        },
        {
          title: 'Duration',
          dataIndex: 'durationMs',
          key: 'durationMs',
          width: 100,
        },
        {
          title: 'Pages',
          dataIndex: 'pagesCount',
          key: 'pagesCount',
          width: 80,
        },
        {
          title: 'Errors',
          key: 'errors',
          width: 100,
        },
        {
          title: 'Actions',
          key: 'actions',
          width: 100,
          fixed: 'right',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      sessions: (state) => state.analytics.sessions,
      sessionsTotal: (state) => state.analytics.sessionsTotal,
      sessionsLoading: (state) => state.analytics.sessionsLoading,
    }),
    filterParams() {
      const params = {
        period: this.filters.period,
        page: this.pagination.page,
        limit: this.pagination.limit,
      };
      if (this.filters.period === 'custom' && this.filters.dateRange.length === 2) {
        params.startDate = this.filters.dateRange[0].toISOString();
        params.endDate = this.filters.dateRange[1].toISOString();
      }
      if (this.filters.deviceType) {
        params.deviceType = this.filters.deviceType;
      }
      if (this.filters.hasErrors !== null) {
        params.hasErrors = this.filters.hasErrors;
      }
      if (this.filters.search) {
        params.search = this.filters.search;
      }
      return params;
    },
    paginationConfig() {
      return {
        current: this.pagination.page,
        pageSize: this.pagination.limit,
        total: this.sessionsTotal,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} sessions`,
      };
    },
  },
  methods: {
    b,
    ...mapActions('analytics', ['getSessions']),
    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).format('MMM DD, YYYY HH:mm');
    },
    formatDuration(ms) {
      if (!ms) return '-';
      const seconds = Math.floor(ms / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
      }
      if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
      }
      return `${seconds}s`;
    },
    getDeviceIcon(deviceType) {
      switch (deviceType) {
        case 'desktop':
          return DesktopOutlined;
        case 'tablet':
          return TabletOutlined;
        case 'mobile':
          return MobileOutlined;
        default:
          return DesktopOutlined;
      }
    },
    loadData() {
      this.getSessions(this.filterParams);
    },
    onFilterChange() {
      this.pagination.page = 1;
      this.loadData();
    },
    onPeriodChange() {
      if (this.filters.period !== 'custom') {
        this.filters.dateRange = [];
        this.onFilterChange();
      }
    },
    onDateRangeChange() {
      if (this.filters.dateRange.length === 2) {
        this.onFilterChange();
      }
    },
    onSearch() {
      this.onFilterChange();
    },
    onTableChange(pagination) {
      this.pagination.page = pagination.current;
      this.pagination.limit = pagination.pageSize;
      this.loadData();
    },
    playSession(sessionId) {
      this.$router.push(`/analytics/sessions/${sessionId}`);
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<template>
  <div :class="b()">
    <page-header :title="title">
      <template #actions>
        <a-button @click="loadData">
          <reload-outlined />
          Refresh
        </a-button>
      </template>
    </page-header>

    <!-- Filters -->
    <a-card :class="b('filters')">
      <a-row :gutter="[16, 16]">
        <a-col :xs="24" :sm="12" :md="6">
          <a-select
            v-model:value="filters.period"
            :options="periodOptions"
            placeholder="Period"
            style="width: 100%"
            @change="onPeriodChange"
          />
        </a-col>
        <a-col v-if="filters.period === 'custom'" :xs="24" :sm="12" :md="6">
          <a-range-picker
            v-model:value="filters.dateRange"
            :show-time="false"
            style="width: 100%"
            @change="onDateRangeChange"
          />
        </a-col>
        <a-col :xs="24" :sm="12" :md="6">
          <a-select
            v-model:value="filters.deviceType"
            :options="deviceTypeOptions"
            placeholder="Device Type"
            style="width: 100%"
            @change="onFilterChange"
          />
        </a-col>
        <a-col :xs="24" :sm="12" :md="6">
          <a-select
            v-model:value="filters.hasErrors"
            :options="hasErrorsOptions"
            placeholder="Error Status"
            style="width: 100%"
            @change="onFilterChange"
          />
        </a-col>
        <a-col :xs="24" :sm="12" :md="6">
          <a-input-search
            v-model:value="filters.search"
            placeholder="Search by email or URL"
            @search="onSearch"
          />
        </a-col>
      </a-row>
    </a-card>

    <!-- Sessions Table -->
    <a-card :class="b('table-card')">
      <a-table
        :dataSource="sessions"
        :columns="columns"
        :loading="sessionsLoading"
        :pagination="paginationConfig"
        :scroll="{ x: 900 }"
        row-key="id"
        @change="onTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'deviceType'">
            <a-tooltip
              :title="`${record.deviceType} - ${record.screenWidth}x${record.screenHeight}`"
            >
              <component :is="getDeviceIcon(record.deviceType)" style="font-size: 20px" />
            </a-tooltip>
          </template>

          <template v-else-if="column.key === 'userEmail'">
            <div>
              <div v-if="record.userEmail">{{ record.userEmail }}</div>
              <div v-else class="text-muted">Anonymous</div>
              <div class="text-small text-muted">{{ record.browser }} / {{ record.os }}</div>
            </div>
          </template>

          <template v-else-if="column.key === 'location'">
            <div v-if="record.country">
              {{ record.city ? `${record.city}, ` : '' }}{{ record.country }}
            </div>
            <div v-else class="text-muted">-</div>
          </template>

          <template v-else-if="column.key === 'startedAt'">
            {{ formatDate(record.startedAt) }}
          </template>

          <template v-else-if="column.key === 'durationMs'">
            {{ formatDuration(record.durationMs) }}
          </template>

          <template v-else-if="column.key === 'errors'">
            <a-space>
              <a-tag v-if="record.jsErrorCount > 0" color="red">
                <warning-outlined /> {{ record.jsErrorCount }} JS
              </a-tag>
              <a-tag v-if="record.apiErrorCount > 0" color="orange">
                {{ record.apiErrorCount }} API
              </a-tag>
              <span v-if="!record.jsErrorCount && !record.apiErrorCount" class="text-muted">-</span>
            </a-space>
          </template>

          <template v-else-if="column.key === 'actions'">
            <a-button type="primary" size="small" @click="playSession(record.id)">
              <play-circle-outlined /> Play
            </a-button>
          </template>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<style lang="scss">
.sessions-list {
  padding: 24px;

  &__filters {
    margin-bottom: 16px;
  }

  &__table-card {
    .text-muted {
      color: #999;
    }

    .text-small {
      font-size: 12px;
    }
  }
}
</style>
